import { useNavigate } from 'react-router-dom'

import { PaginatedResponse, RecordType } from '@factorialco/f0-react'
import { OneDataCollection, useDataCollectionSource } from '@factorialco/f0-react/dist/experimental'
import { ExternalLink } from '@factorialco/f0-react/icons/app'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

interface EmptyCostCentersProps {
  hasConfigurationPending: boolean
  canManageCostCenters: boolean
  hasWorkplaces: boolean
  hasJobCatalog: boolean
  startConfiguration: () => void
}

// We use DataCollection here to check if any cost centers exist before rendering
// the employee assignments list. This ensures consistent styling and automatic
// updates when DataCollection styles change. The empty state redirects users to
// create their first cost center, while the main collection displays employees
// with their cost center assignments (current and future).
export const EmptyCostCentersStructure = ({
  hasConfigurationPending,
  canManageCostCenters,
  hasWorkplaces,
  hasJobCatalog,
  startConfiguration,
}: EmptyCostCentersProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const noDataEmoji = useUnsafeMemo(() => {
    if (hasConfigurationPending) return '👏'
  }, [hasConfigurationPending])

  const yes_jc_yes_wp = hasJobCatalog && hasWorkplaces
  const no_jc_yes_wp = !hasJobCatalog && hasWorkplaces
  const yes_jc_no_wp = hasJobCatalog && !hasWorkplaces
  const no_jc_no_wp = !hasJobCatalog && !hasWorkplaces

  const noDataTitle = useUnsafeMemo(() => {
    if (hasConfigurationPending)
      return i18n.t('finance.cost_centers_structure.empty.has_configuration.title')
    if (yes_jc_yes_wp) return i18n.t('finance.cost_centers_structure.empty.yes_jc_yes_wp.title')
    if (no_jc_yes_wp) return i18n.t('finance.cost_centers_structure.empty.no_jc_yes_wp.title')
    if (yes_jc_no_wp) return i18n.t('finance.cost_centers_structure.empty.yes_jc_no_wp.title')
    if (no_jc_no_wp) return i18n.t('finance.cost_centers_structure.empty.no_jc_no_wp.title')
  }, [hasConfigurationPending, yes_jc_yes_wp, no_jc_yes_wp, yes_jc_no_wp, no_jc_no_wp])

  const noDataDescription = useUnsafeMemo(() => {
    if (hasConfigurationPending)
      return i18n.t('finance.cost_centers_structure.empty.has_configuration.description')

    if (!canManageCostCenters) return ''

    if (yes_jc_yes_wp)
      return i18n.t('finance.cost_centers_structure.empty.yes_jc_yes_wp.description')
    if (no_jc_yes_wp) return i18n.t('finance.cost_centers_structure.empty.no_jc_yes_wp.description')
    if (yes_jc_no_wp) return i18n.t('finance.cost_centers_structure.empty.yes_jc_no_wp.description')
    if (no_jc_no_wp) return i18n.t('finance.cost_centers_structure.empty.no_jc_no_wp.description')
  }, [
    hasConfigurationPending,
    yes_jc_yes_wp,
    no_jc_yes_wp,
    yes_jc_no_wp,
    no_jc_no_wp,
    canManageCostCenters,
  ])

  const noDataActions = useUnsafeMemo(() => {
    if (!canManageCostCenters || hasConfigurationPending) return []

    const actions = []

    if (yes_jc_yes_wp) {
      actions.push({
        label: i18n.t('finance.cost_centers_structure.empty.yes_jc_yes_wp.configure_cc'),
        onClick: () => {
          startConfiguration()
        },
      })
    }

    if (no_jc_yes_wp) {
      actions.push({
        icon: ExternalLink,
        variant: 'outline' as const,
        label: i18n.t('finance.cost_centers_structure.empty.no_jc_yes_wp.configure_jc'),
        onClick: () => {
          navigate(navigation.employees.jobCatalog.fullPath())
        },
      })

      actions.push({
        label: i18n.t('finance.cost_centers_structure.empty.no_jc_yes_wp.configure_cc'),
        onClick: () => {
          startConfiguration()
        },
      })
    }

    if (yes_jc_no_wp) {
      actions.push({
        icon: ExternalLink,
        variant: 'outline' as const,
        label: i18n.t('finance.cost_centers_structure.empty.yes_jc_no_wp.configure_wp'),
        onClick: () => {
          navigate(navigation.settings.locations.fullPath())
        },
      })

      actions.push({
        label: i18n.t('finance.cost_centers_structure.empty.yes_jc_no_wp.configure_cc'),
        onClick: () => {
          startConfiguration()
        },
      })
    }

    if (no_jc_no_wp) {
      actions.push({
        icon: ExternalLink,
        variant: 'outline' as const,
        label: i18n.t('finance.cost_centers_structure.empty.no_jc_no_wp.configure_wp'),
        onClick: () => {
          navigate(navigation.settings.locations.fullPath())
        },
      })

      actions.push({
        icon: ExternalLink,
        variant: 'outline' as const,
        label: i18n.t('finance.cost_centers_structure.empty.no_jc_no_wp.configure_jc'),
        onClick: () => {
          navigate(navigation.employees.jobCatalog.fullPath())
        },
      })
    }

    return actions
  }, [
    navigate,
    navigation,
    no_jc_no_wp,
    no_jc_yes_wp,
    yes_jc_no_wp,
    yes_jc_yes_wp,
    startConfiguration,
    canManageCostCenters,
    hasConfigurationPending,
  ])

  const secondaryActions = useUnsafeMemo(() => {
    if (yes_jc_yes_wp && canManageCostCenters && !hasConfigurationPending) {
      return {
        expanded: 0 as const,
        actions: () => [
          {
            icon: ExternalLink,
            label: i18n.t('finance.cost_centers_structure.empty.yes_jc_yes_wp.configure_jc'),
            onClick: () => {
              navigate(navigation.employees.jobCatalog.fullPath())
            },
          },
          {
            icon: ExternalLink,
            label: i18n.t('finance.cost_centers_structure.empty.yes_jc_yes_wp.configure_wp'),
            onClick: () => {
              navigate(navigation.settings.locations.fullPath())
            },
          },
        ],
      }
    }
  }, [yes_jc_yes_wp, canManageCostCenters, hasConfigurationPending])

  const source = useDataCollectionSource({
    secondaryActions,
    dataAdapter: {
      fetchData: (): Promise<PaginatedResponse<RecordType>> => {
        return Promise.resolve({
          type: 'pages',
          records: [],
          total: 0,
          currentPage: 1,
          perPage: 10,
          pagesCount: 1,
        })
      },
      perPage: 10,
      paginationType: 'pages',
    },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[{ type: 'table', options: { columns: [] } }]}
      emptyStates={{
        'no-data': {
          title: noDataTitle,
          emoji: noDataEmoji,
          actions: noDataActions,
          description: noDataDescription,
        },
      }}
    />
  )
}
